import React, { useState, useEffect } from 'react';
import { TTIRequestLog } from '../types';
import { ttiLogService } from '../services/ttiLogService';

interface TTIRequestLogViewerProps {
  batchImportId?: number;
  batchImportItemId?: number;
}

const TTIRequestLogViewer: React.FC<TTIRequestLogViewerProps> = (props: TTIRequestLogViewerProps) => {
  const { batchImportId, batchImportItemId } = props;
  const [logs, setLogs] = useState<TTIRequestLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<TTIRequestLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const loadLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (batchImportId && batchImportItemId) {
        response = await ttiLogService.getBatchImportItemLogs(batchImportId, batchImportItemId);
      } else if (batchImportId) {
        response = await ttiLogService.getBatchImportLogs(batchImportId);
      } else {
        response = await ttiLogService.getAllLogs();
      }
      setLogs(response.logs);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load TTI logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchImportId, batchImportItemId]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const formatJSON = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">TTI API Request Logs</h3>
        <span className="text-sm text-gray-600">{logs.length} request(s)</span>
      </div>

      {logs.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">No TTI requests logged yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {logs.map((log: TTIRequestLog) => (
            <div
              key={log.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedLog?.id === log.id
                  ? 'border-primary-500 bg-primary-50'
                  : log.success
                  ? 'border-green-200 bg-green-50 hover:border-green-300'
                  : 'border-red-200 bg-red-50 hover:border-red-300'
              }`}
              onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      log.success
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.success ? '✓ Success' : '✗ Failed'}
                  </span>

                  <span className="font-mono text-sm font-semibold">
                    {log.request_method}
                  </span>

                  <span className="text-sm text-gray-700 font-medium">
                    {log.operation_type}
                  </span>

                  {log.response_status_code && (
                    <span
                      className={`text-sm font-medium ${
                        log.response_status_code < 400
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      HTTP {log.response_status_code}
                    </span>
                  )}

                  {log.duration_ms && (
                    <span className="text-xs text-gray-500">
                      {log.duration_ms}ms
                    </span>
                  )}
                </div>

                <span className="text-xs text-gray-500">
                  {new Date(log.created_at).toLocaleString()}
                </span>
              </div>

              {log.error_message && (
                <div className="mt-2 text-sm text-red-700">
                  <strong>Error:</strong> {log.error_message}
                </div>
              )}

              {selectedLog?.id === log.id && (
                <div className="mt-4 space-y-4 border-t pt-4">
                  {/* Request Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">Request</h4>
                      <button
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          copyToClipboard(formatJSON({
                            method: log.request_method,
                            url: log.request_url,
                            headers: log.request_headers,
                            body: log.request_body,
                          }));
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Copy Request
                      </button>
                    </div>

                    <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-x-auto">
                      <div><strong className="text-yellow-400">URL:</strong> {log.request_url}</div>
                      {log.request_body && (
                        <div className="mt-2">
                          <strong className="text-yellow-400">Body:</strong>
                          <pre className="mt-1">{formatJSON(log.request_body)}</pre>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Response Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">Response</h4>
                      <button
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          copyToClipboard(formatJSON({
                            status_code: log.response_status_code,
                            headers: log.response_headers,
                            body: log.response_body,
                          }));
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Copy Response
                      </button>
                    </div>

                    <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-x-auto">
                      <div>
                        <strong className="text-yellow-400">Status:</strong>{' '}
                        {log.response_status_code || 'N/A'}
                      </div>
                      {log.response_body && (
                        <div className="mt-2">
                          <strong className="text-yellow-400">Body:</strong>
                          <pre className="mt-1">{formatJSON(log.response_body)}</pre>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Postman Export Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        const postmanCollection = {
                          info: {
                            name: `TTI Request - ${log.operation_type}`,
                            description: `Exported from TTI Request Log #${log.id}`,
                            schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
                          },
                          item: [
                            {
                              name: log.operation_type,
                              request: {
                                method: log.request_method,
                                header: log.request_headers
                                  ? Object.entries(log.request_headers).map(([key, value]) => ({
                                      key,
                                      value: String(value),
                                    }))
                                  : [],
                                body: log.request_body
                                  ? {
                                      mode: 'raw',
                                      raw: formatJSON(log.request_body),
                                      options: {
                                        raw: {
                                          language: 'json',
                                        },
                                      },
                                    }
                                  : undefined,
                                url: {
                                  raw: log.request_url,
                                  protocol: log.request_url.split('://')[0],
                                  host: log.request_url.split('://')[1].split('/')[0].split('.'),
                                  path: log.request_url.split('://')[1].split('/').slice(1),
                                },
                              },
                            },
                          ],
                        };

                        const blob = new Blob([JSON.stringify(postmanCollection, null, 2)], {
                          type: 'application/json',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `tti-request-${log.id}.postman_collection.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm font-medium"
                    >
                      📮 Export to Postman
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TTIRequestLogViewer;

